/**
 * Data Quality detection controls.
 *
 * Compact panel above the grid: four rules (variance %, z-score σ, PoP %,
 * gap detection) each with an enable toggle and a warning/critical pair.
 * Tweaking any value re-runs detection in the parent and re-colours dots
 * across the grid in place.
 *
 * Layout intent: not a wall of inputs. Each rule is a single row with the
 * toggle + warn/crit numeric pair, plus a one-line description so users
 * remember what each does without leaving to a docs page.
 */

import { cn } from '@g17eco/ui-modern/lib/utils';
import {
  type DetectionThresholds,
} from './dataQualityDetection';

interface DataQualityControlsProps {
  thresholds: DetectionThresholds;
  onChange: (next: DetectionThresholds) => void;
  /** Master on/off for gap detection — gaps live outside `DetectionThresholds`. */
  gapDetectionEnabled: boolean;
  onGapDetectionChange: (enabled: boolean) => void;
}

interface NumericPairProps {
  label: string;
  description: string;
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  warnValue: number;
  warnSuffix: string;
  warnStep?: number;
  warnMin?: number;
  onWarnChange: (next: number) => void;
  critValue: number;
  critSuffix: string;
  critStep?: number;
  critMin?: number;
  onCritChange: (next: number) => void;
}

const inputClass =
  'tw:h-7 tw:w-16 tw:rounded tw:border tw:border-input tw:bg-transparent tw:px-2 tw:text-xs tw:tabular-nums tw:focus:outline-none tw:focus:ring-1 tw:focus:ring-ring';

const NumericPair = ({
  label,
  description,
  enabled,
  onEnabledChange,
  warnValue,
  warnSuffix,
  warnStep = 1,
  warnMin = 0,
  onWarnChange,
  critValue,
  critSuffix,
  critStep = 1,
  critMin = 0,
  onCritChange,
}: NumericPairProps) => (
  <div className={cn('tw:py-2', !enabled && 'tw:opacity-60')}>
    <div className='tw:flex tw:items-center tw:gap-3 tw:flex-wrap'>
      <label className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:cursor-pointer tw:min-w-[180px]'>
        <input
          type='checkbox'
          checked={enabled}
          onChange={(e) => onEnabledChange(e.target.checked)}
          className='tw:size-4 tw:cursor-pointer'
        />
        {label}
      </label>
      <div className='tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground'>
        <span>warn ≥</span>
        <input
          type='number'
          value={warnValue}
          step={warnStep}
          min={warnMin}
          disabled={!enabled}
          onChange={(e) => onWarnChange(Number(e.target.value))}
          className={inputClass}
        />
        <span>{warnSuffix}</span>
      </div>
      <div className='tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground'>
        <span>critical ≥</span>
        <input
          type='number'
          value={critValue}
          step={critStep}
          min={critMin}
          disabled={!enabled}
          onChange={(e) => onCritChange(Number(e.target.value))}
          className={inputClass}
        />
        <span>{critSuffix}</span>
      </div>
    </div>
    <p className='tw:text-xs tw:text-muted-foreground tw:mt-1 tw:ml-6 tw:mb-0'>{description}</p>
  </div>
);

export const DataQualityControls = ({
  thresholds,
  onChange,
  gapDetectionEnabled,
  onGapDetectionChange,
}: DataQualityControlsProps) => {
  const update = (patch: Partial<DetectionThresholds>) => onChange({ ...thresholds, ...patch });

  return (
    <div className='tw:divide-y tw:divide-border tw:py-0 tw:ps-4'>
      <NumericPair
        label='Variance vs rolling average'
        description='Flag months whose value deviates from the prior 3-month average by more than the threshold.'
        enabled={thresholds.varianceEnabled}
        onEnabledChange={(v) => update({ varianceEnabled: v })}
        warnValue={thresholds.variancePct}
        warnSuffix='%'
        onWarnChange={(v) => update({ variancePct: v })}
        critValue={thresholds.varianceCriticalPct}
        critSuffix='%'
        onCritChange={(v) => update({ varianceCriticalPct: v })}
      />
      <NumericPair
        label='Z-score (row outliers)'
        description='Flag months whose value is more than σ standard deviations from the row mean.'
        enabled={thresholds.zScoreEnabled}
        onEnabledChange={(v) => update({ zScoreEnabled: v })}
        warnValue={thresholds.zScoreSigma}
        warnSuffix='σ'
        warnStep={0.1}
        onWarnChange={(v) => update({ zScoreSigma: v })}
        critValue={thresholds.zScoreCriticalSigma}
        critSuffix='σ'
        critStep={0.1}
        onCritChange={(v) => update({ zScoreCriticalSigma: v })}
      />
      <NumericPair
        label='Period-over-period jump'
        description='Flag months whose value differs from the previous month by more than the threshold.'
        enabled={thresholds.popEnabled}
        onEnabledChange={(v) => update({ popEnabled: v })}
        warnValue={thresholds.popPct}
        warnSuffix='%'
        onWarnChange={(v) => update({ popPct: v })}
        critValue={thresholds.popCriticalPct}
        critSuffix='%'
        onCritChange={(v) => update({ popCriticalPct: v })}
      />
      <div className='tw:py-2'>
        <label className='tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:cursor-pointer'>
          <input
            type='checkbox'
            checked={gapDetectionEnabled}
            onChange={(e) => onGapDetectionChange(e.target.checked)}
            className='tw:size-4 tw:cursor-pointer'
          />
          Missing-data gaps
        </label>
        <p className='tw:text-xs tw:text-muted-foreground tw:mt-1 tw:ml-6 tw:mb-0'>
          Mark cells with no activity recorded for that source × month with a red cross.
        </p>
      </div>
    </div>
  );
};
